var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Control } from './Control';
import PanelsContainerControlTemplate from '@front/templates/PanelsContainerControlTemplate.html';
import { TerceraWindowBase } from '../screen/TerceraWindowBase';
import { Resources } from '@shared/localizations/Resources';
class PanelsContainerControlTabItem {
    constructor(text, isVisible, panel) {
        this.text = text;
        this.isVisible = isVisible;
        this.panel = panel;
    }
}
export class PanelsContainerControl extends Control {
    constructor() {
        super();
        this._visiblePanelsCount = 0;
        this.onTabItemClicked = this.onTabItemClicked.bind(this);
        this.onCollapseButtonClicked = this.onCollapseButtonClicked.bind(this);
        this.onCollapsedChanged = this.onCollapsedChanged.bind(this);
        this.onSelectedTabItemChanged = this.onSelectedTabItemChanged.bind(this);
        this.onPanelHeaderChanged = this.onPanelHeaderChanged.bind(this);
        this.onPanelForbiddenChanged = this.onPanelForbiddenChanged.bind(this);
    }
    oninit() {
        super.oninit();
        super.on('onTabItemClick', this.onTabItemClicked);
        super.on('onCollapseButtonClick', this.onCollapseButtonClicked);
        super.observe('isCollapsed', this.onCollapsedChanged);
        super.observe('selectedTabItem', this.onSelectedTabItemChanged);
    }
    oncomplete() {
        const _super = Object.create(null, {
            oncomplete: { get: () => super.oncomplete },
            findAllComponents: { get: () => super.findAllComponents },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.oncomplete.call(this);
            const panels = _super.findAllComponents.call(this).filter((component) => component instanceof TerceraWindowBase);
            for (let i = 0; i < panels.length; i++) {
                panels[i].observe('header', this.onPanelHeaderChanged);
                panels[i].observe('forbiddenPanel', this.onPanelForbiddenChanged);
            }
            const tabItems = [];
            for (let i = 0; i < panels.length; i++) {
                const isForbiddenPanel = panels[i].get('forbiddenPanel');
                const tabItem = new PanelsContainerControlTabItem(panels[i].get('header'), !isForbiddenPanel, panels[i]);
                tabItems.push(tabItem);
            }
            yield _super.set.call(this, 'tabItems', tabItems);
            yield this.onTabItemClicked(null, tabItems.find((tabItem) => tabItem.isVisible));
            this.updateVisiblePanelsCount();
        });
    }
    localize() {
        this.updateTooltip();
    }
    onPanelHeaderChanged() {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const tabItems = _super.get.call(this, 'tabItems');
            for (let i = 0; i < tabItems.length; i++) {
                const panel = tabItems[i].panel;
                tabItems[i].text = panel.get('header');
            }
            yield _super.set.call(this, 'tabItems', tabItems);
        });
    }
    onPanelForbiddenChanged() {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const tabItems = _super.get.call(this, 'tabItems');
            if (!isValidArray(tabItems)) {
                return;
            }
            for (let i = 0; i < tabItems.length; i++) {
                const panel = tabItems[i].panel;
                const isForbidden = panel.get('forbiddenPanel');
                tabItems[i].isVisible = !isForbidden;
            }
            yield _super.set.call(this, 'tabItems', tabItems);
            const selectedTabItem = _super.get.call(this, 'selectedTabItem');
            if (!isNullOrUndefined(selectedTabItem) && !selectedTabItem.isVisible) {
                const visibleTabItem = tabItems.find((tabItem) => tabItem.isVisible);
                yield this.onTabItemClicked(null, visibleTabItem);
            }
            this.updateVisiblePanelsCount();
        });
    }
    onTabItemClicked(event, tabItem) {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const isCollapsed = _super.get.call(this, 'isCollapsed');
            if (isCollapsed) {
                this.onCollapseButtonClicked();
            }
            const forcedSelectedPanel = this.get('forcedSelectedPanel');
            if (!isNullOrUndefined(forcedSelectedPanel)) {
                this.onForcedSelectedItem(forcedSelectedPanel);
            }
            else {
                void _super.set.call(this, 'selectedTabItem', tabItem);
            }
        });
    }
    onForcedSelectedItem(forcedSelectedPanel) {
        const tabItems = super.get('tabItems');
        if (!isValidArray(tabItems)) {
            return;
        }
        for (const tabItem of tabItems) {
            if (tabItem.panel === forcedSelectedPanel) {
                void super.set({
                    forcedSelectedPanel: null,
                    selectedTabItem: tabItem
                });
            }
        }
    }
    onSelectedTabItemChanged() {
        const _super = Object.create(null, {
            get: { get: () => super.get }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const tabItems = _super.get.call(this, 'tabItems');
            if (!isValidArray(tabItems)) {
                return;
            }
            const selectedTabItem = _super.get.call(this, 'selectedTabItem');
            for (let i = 0; i < tabItems.length; i++) {
                if (tabItems[i] !== selectedTabItem) {
                    yield tabItems[i].panel.set('visible', false);
                }
            }
            if (!isNullOrUndefined(selectedTabItem)) {
                yield selectedTabItem.panel.set('visible', true);
            }
            this.fire('activePanelChange');
        });
    }
    onCollapseButtonClicked() {
        const isCollapsed = super.get('isCollapsed');
        void super.set('isCollapsed', !isCollapsed);
    }
    onCollapsedChanged() {
        const isCollapsed = super.get('isCollapsed');
        const selectedTabItem = super.get('selectedTabItem');
        if (!isNullOrUndefined(selectedTabItem)) {
            selectedTabItem.panel.set('visible', !isCollapsed);
        }
        this.fire('collapsedChange', isCollapsed);
        this.updateTooltip();
    }
    updateTooltip() {
        const isCollapsed = super.get('isCollapsed');
        const tooltip = isCollapsed
            ? Resources.getResource('property.ShowAdditionalPanels')
            : Resources.getResource('property.HideAdditionalPanels');
        void super.set('infoBtnTooltip', tooltip);
    }
    updateVisiblePanelsCount() {
        const tabItems = super.get('tabItems');
        if (!isValidArray(tabItems)) {
            return;
        }
        let visiblePanelsCount = 0;
        for (let i = 0; i < tabItems.length; i++) {
            const tabItem = tabItems[i];
            if (tabItem.isVisible) {
                visiblePanelsCount++;
            }
        }
        if (this._visiblePanelsCount !== visiblePanelsCount) {
            this._visiblePanelsCount = visiblePanelsCount;
            this.fire('visiblePanelsCountChange', this._visiblePanelsCount);
        }
    }
}
Control.extendWith(PanelsContainerControl, {
    template: PanelsContainerControlTemplate,
    data() {
        return {
            isVisibleTabs: true,
            infoBtnTooltip: '',
            isShowCollapsedButton: false,
            isCollapsed: false,
            selectedTabItem: null,
            tabItems: []
        };
    }
});
